import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LEARN } from "../../theme";
import { ConfettiBurst } from "../../feedback/Celebration";
import { itemKey, seededShuffle, useCompleteOnce } from "./engineUtils";
import { Art, Counter, ROOT_STYLE, tileSize, useLetterAccent } from "./tapUtils";

/**
 * Bubbles drift up through the whole arena with word art inside. Pop only
 * the target-letter pictures; distractor bubbles wobble and count a wrong.
 * Completes after `targetCount` target pops.
 */
export default function BubblePopGame({ letter, config = {}, speak, sfx, onComplete, onWrong, reduceMotion }) {
  const finish = useCompleteOnce(onComplete);
  const { accent } = useLetterAccent(letter);
  const need = config.targetCount || 6;

  const bubbles = useMemo(() => {
    const t = seededShuffle(config.targetItems || [], 4).slice(0, need);
    const d = seededShuffle(config.distractorItems || [], 8).slice(0, config.distractorCount || 3);
    const all = seededShuffle(
      [...t.map((item) => ({ item, isTarget: true })), ...d.map((item) => ({ item, isTarget: false }))],
      12,
    );
    const n = Math.max(all.length, 1);
    return all.map((b, i) => ({
      ...b,
      id: `${itemKey(b.item)}-${i}`,
      // four lanes across the width, a little jitter so rows never line up
      left: 16 + (i % 4) * 22.6 + (((i * 7) % 5) - 2) * 1.5,
      start: 100 - (i / n) * 100, // first pass starts staggered up the arena
      size: 1.2 + (i % 3) * 0.12,
      duration: 11 + ((i * 5) % 4) * 1.6,
      sway: 8 + (i % 4) * 4,
    }));
  }, [config.targetItems, config.distractorItems, config.distractorCount, need]);

  const [popped, setPopped] = useState(() => new Set());
  const [wrongTick, setWrongTick] = useState({});
  const [bursts, setBursts] = useState([]);
  const arenaRef = useRef(null);
  const timers = useRef([]);
  const poppedTargets = bubbles.filter((b) => b.isTarget && popped.has(b.id)).length;
  const done = poppedTargets >= need && need > 0;

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  useEffect(() => {
    if (!done) return undefined;
    sfx?.("sparkle");
    const t = setTimeout(finish, reduceMotion ? 250 : 900);
    return () => clearTimeout(t);
  }, [done, finish, reduceMotion, sfx]);

  const onPop = useCallback(
    (b, e) => {
      if (done || popped.has(b.id)) return;
      if (!b.isTarget) {
        setWrongTick((w) => ({ ...w, [b.id]: (w[b.id] || 0) + 1 }));
        onWrong?.();
        return;
      }
      sfx?.("pop");
      speak?.(b.item.word);
      const arena = arenaRef.current?.getBoundingClientRect();
      const r = e?.currentTarget?.getBoundingClientRect?.();
      if (arena && r) {
        const id = `${b.id}-burst-${Date.now()}`;
        setBursts((list) => [...list, { id, x: r.left + r.width / 2 - arena.left, y: r.top + r.height / 2 - arena.top, w: r.width }]);
        timers.current.push(setTimeout(() => setBursts((list) => list.filter((x) => x.id !== id)), 800));
      }
      setPopped((s) => new Set(s).add(b.id));
    },
    [done, popped, onWrong, sfx, speak],
  );

  return (
    <div style={ROOT_STYLE}>
      <Counter done={poppedTargets} total={need} accent={accent} suffix="popped" />
      <div ref={arenaRef} style={{ position: "absolute", inset: 0, overflow: "hidden", borderRadius: LEARN.radius.lg }}>
        <ConfettiBurst active={done} originY={45} count={36} reduceMotion={reduceMotion} />
        <AnimatePresence>
          {bubbles.map((b) => (!popped.has(b.id) ? (
            <Bubble key={b.id} b={b} letter={letter} reduceMotion={reduceMotion} wrongTick={wrongTick[b.id] || 0} onPop={onPop} accent={accent} />
          ) : null))}
        </AnimatePresence>
        {!reduceMotion && bursts.map((bu) => <PopRing key={bu.id} x={bu.x} y={bu.y} w={bu.w} accent={accent} />)}
      </div>
    </div>
  );
}

function Bubble({ b, letter, reduceMotion, wrongTick, onPop, accent }) {
  // Each pass: from `start` (first pass) or 110% (later passes) up to -30%.
  const [pass, setPass] = useState(0);
  const from = pass === 0 ? b.start : 110;
  const dur = (b.duration * (from + 30)) / 140;
  const staticTop = 10 + ((b.start * 0.7) % 70);

  return (
    <motion.div
      key={`${b.id}-${pass}`}
      initial={{ top: reduceMotion ? `${staticTop}%` : `${from}%` }}
      animate={reduceMotion ? { top: `${staticTop}%` } : { top: "-30%" }}
      transition={reduceMotion ? { duration: 0 } : { duration: dur, ease: "linear" }}
      onAnimationComplete={() => { if (!reduceMotion) setPass((p) => p + 1); }}
      exit={{ scale: 1.5, opacity: 0, transition: { duration: reduceMotion ? 0 : 0.22 } }}
      style={{ position: "absolute", left: `${b.left}%`, width: tileSize(b.size), height: tileSize(b.size), marginLeft: `calc(${tileSize(b.size)} / -2)` }}
    >
      <motion.div
        animate={reduceMotion ? { x: 0, rotate: 0 } : { x: [-b.sway, b.sway, -b.sway], rotate: [-3, 3, -3] }}
        transition={reduceMotion ? { duration: 0 } : { duration: 3.2 + (b.sway % 3), repeat: Infinity, ease: "easeInOut" }}
        style={{ width: "100%", height: "100%" }}
      >
        <motion.button
          type="button"
          aria-label={b.item.word}
          onClick={(e) => onPop(b, e)}
          key={`wobble-${wrongTick}`}
          animate={wrongTick && !reduceMotion ? { x: [0, -8, 8, -5, 5, 0] } : { x: 0 }}
          transition={{ duration: 0.4 }}
          whileTap={reduceMotion ? undefined : { scale: 0.92 }}
          style={{
            width: "100%", height: "100%", borderRadius: "50%", padding: 0, cursor: "pointer",
            border: `2px solid rgba(255,255,255,0.95)`,
            background: `radial-gradient(circle at 32% 28%, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.55) 22%, ${LEARN.color.sky}cc 60%, ${accent}66 100%)`,
            boxShadow: `inset 0 -10px 20px rgba(255,255,255,0.45), inset 0 8px 14px rgba(255,255,255,0.6), 0 12px 24px rgba(11,26,51,0.16)${wrongTick ? `, 0 0 0 3px ${LEARN.color.coral}` : ""}`,
            display: "flex", alignItems: "center", justifyContent: "center", position: "relative",
            WebkitTapHighlightColor: "transparent", touchAction: "manipulation", outline: "none",
          }}
        >
          <span aria-hidden="true" style={{ position: "absolute", left: "18%", top: "12%", width: "26%", height: "16%", borderRadius: "50%", background: "rgba(255,255,255,0.85)", transform: "rotate(-30deg)", filter: "blur(1px)" }} />
          <Art letter={letter} assetName={b.item.assetName} word={b.item.word} alt="" style={{ width: "66%", height: "66%" }} />
        </motion.button>
      </motion.div>
    </motion.div>
  );
}

function PopRing({ x, y, w, accent }) {
  const drops = Array.from({ length: 7 }, (_, i) => ({ i, a: (i / 7) * Math.PI * 2, d: w * 0.65 + (i % 3) * 8 }));
  return (
    <span aria-hidden="true" style={{ position: "absolute", left: x, top: y, width: 0, height: 0, pointerEvents: "none", zIndex: 4 }}>
      <motion.span
        initial={{ scale: 0.6, opacity: 0.9 }}
        animate={{ scale: 1.7, opacity: 0 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
        style={{ position: "absolute", left: -w / 2, top: -w / 2, width: w, height: w, borderRadius: "50%", border: "3px solid rgba(255,255,255,0.95)", boxShadow: `0 0 0 2px ${accent}55` }}
      />
      {drops.map((p) => (
        <motion.span
          key={p.i}
          initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
          animate={{ x: Math.cos(p.a) * p.d, y: Math.sin(p.a) * p.d + 18, opacity: 0, scale: 0.3 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          style={{ position: "absolute", left: -5, top: -5, width: 10, height: 10, borderRadius: "50%", background: "rgba(255,255,255,0.95)", boxShadow: `0 0 8px ${LEARN.color.sky}` }}
        />
      ))}
    </span>
  );
}
